import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { CompanyPortfolioState } from '../types/portfolioTypes';

interface FetchServicesParams {
  companyNumber: string;
  token: string;
}

const initialState: CompanyPortfolioState = {
  data: null,
  loading: false,
  error: null,
};

export const fetchPortfolioServices = createAsyncThunk(
  'companyPortfolio/fetchServices',
  async ({ companyNumber, token }: FetchServicesParams, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_SERVICES}/api/v1/services/allservices/${companyNumber}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch company services');

      const result = await response.json();
      if (result.status === 'success') {
        return result.data;
      }
      throw new Error('Invalid data structure received');
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

const companyPortfolioSlice = createSlice({
  name: 'companyPortfolio',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPortfolioServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPortfolioServices.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchPortfolioServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Selectors
export const selectPortfolioServices = (state: { companyPortfolio: CompanyPortfolioState }) =>
  state.companyPortfolio.data;
export const selectInjectionServices = (state: { companyPortfolio: CompanyPortfolioState }) =>
  state.companyPortfolio.data?.services.injection;
export const selectOfftakeServices = (state: { companyPortfolio: CompanyPortfolioState }) =>
  state.companyPortfolio.data?.services.offtake;
export const selectBessServices = (state: { companyPortfolio: CompanyPortfolioState }) =>
  state.companyPortfolio.data?.services.bess;
export const selectEboilerServices = (state: { companyPortfolio: CompanyPortfolioState }) =>
  state.companyPortfolio.data?.services.eboiler;
export const selectPvCurtailmentServices = (state: { companyPortfolio: CompanyPortfolioState }) =>
  state.companyPortfolio.data?.services.pvCurtailment;
export const selectIsLoading = (state: { companyPortfolio: CompanyPortfolioState }) => state.companyPortfolio.loading;
export const selectError = (state: { companyPortfolio: CompanyPortfolioState }) => state.companyPortfolio.error;

export const { clearError, resetState } = companyPortfolioSlice.actions;
export default companyPortfolioSlice.reducer;
